import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BuildingOffice2Icon, HeartIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import SEO from '../components/SEO';

const EVENT_FORM_URL = 'https://forms.gle/N6tRiwXk2fyRLKWZ9';

const Activities = () => {
  const { t } = useTranslation();
  const whatsappNumber = '919763303025';

  const sections = [
    {
      titleKey: 'activities.weddings',
      icon: HeartIcon,
      descKey: 'activities.weddingsDesc',
      bulletsKey: 'activities.weddingsBullets',
    },
    {
      titleKey: 'activities.corporateEvents',
      icon: BuildingOffice2Icon,
      descKey: 'activities.corporateDesc',
      bulletsKey: 'activities.corporateBullets',
    },
    {
      titleKey: 'activities.carnivalsExhibitions',
      icon: SparklesIcon,
      descKey: 'activities.carnivalsDesc',
      bulletsKey: 'activities.carnivalsBullets',
    },
  ];

  const getBullets = (bulletsKey) => [t(`${bulletsKey}.1`), t(`${bulletsKey}.2`), t(`${bulletsKey}.3`)];

  const getWhatsAppMessage = (activityTitle) =>
    `Hello Kamal Institute, I’m interested in your Activities for “${activityTitle}”. Please share details, pricing, and availability.`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Event Planning and Jewellery Craftsmanship Activities",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Kamal Institute",
      "url": "https://kamalinstitute.co.in"
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Event Activities",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Wedding Jewellery Activities"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Corporate Event Activities"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Carnival & Exhibition Activities"
          }
        }
      ]
    }
  };

  return (
    <div className="bg-white pb-16">
      <SEO
        title="Jewellery Activities for Weddings, Corporates & Events"
        description="We support event experiences that highlight handmade jewellery craftsmanship for weddings, corporates, carnivals, and exhibitions. Custom activity formats for your event."
        keywords="wedding jewellery activities, corporate event activities, jewellery exhibition, jewellery carnival, event planning, jewellery demonstration, handmade jewellery showcase"
        url="/activities"
        structuredData={structuredData}
      />
      <div className="bg-gradient-to-b from-gold-100/20 py-16 pt-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              {t('activities.title')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('activities.subtitle')}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href={EVENT_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md bg-gold-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-gold-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600 transition-all"
              >
                {t('activities.enquireNow')}
              </a>
              <Link
                to="/courses"
                className="text-base font-semibold leading-6 text-gray-900 hover:text-gold-600 transition-colors"
              >
                {t('activities.exploreCourses')} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {sections.map((s) => (
            <div key={s.titleKey} className="rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-900/10">
              <div className="flex items-center gap-3">
                <s.icon className="h-6 w-6 text-gold-600" aria-hidden="true" />
                <h2 className="text-xl font-bold text-gray-900">{t(s.titleKey)}</h2>
              </div>
              <p className="mt-4 text-gray-700 leading-7">{t(s.descKey)}</p>
              <ul className="mt-6 space-y-3 text-sm leading-6 text-gray-700">
                {getBullets(s.bulletsKey).map((b) => (
                  <li key={b} className="flex gap-x-3">
                    <span className="text-gold-600">✓</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3">
                <a
                  href={buildWhatsAppUrl({
                    phoneNumber: whatsappNumber,
                    message: getWhatsAppMessage(t(s.titleKey)),
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-green-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  {t('activities.contactWhatsApp')}
                </a>
                <a
                  href={EVENT_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors"
                >
                  {t('activities.registerForEvent')}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-7xl px-6 lg:px-8">
        <div className="rounded-3xl bg-gray-900 px-6 py-14 sm:px-16 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('activities.planEvent')}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-300">
            Share your date, city, and event type. We’ll suggest the best-fit activity format and flow.
          </p>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <a
              href={EVENT_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-sm hover:bg-gray-100 transition-colors"
            >
              {t('activities.registerForEvent')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activities;

